import React, { useEffect, useMemo, useState } from "react"
import { Filter, Loader2, X } from "lucide-react"
import { listStudentSubjects } from "../../../../api/studentSubjects"
import { normalizeYearInput } from "../../../../utils/yearValidation"
import type { QuestionListFilter, StudentSubject } from "../types"
import { EMPTY_LEVEL_VALUES } from "./LearnerLevelSelectors"
import LearnerTopicTree from "./LearnerTopicTree"
import "./QuestionsFilterPanel.style.scss"

interface QuestionsFilterPanelProps {
  open: boolean
  draft: QuestionListFilter
  onChange: (next: QuestionListFilter) => void
  onApply: () => void
  onClose: () => void
  onClear: () => void
}

const QuestionsFilterPanel = ({
  open,
  draft,
  onChange,
  onApply,
  onClose,
  onClear,
}: QuestionsFilterPanelProps) => {
  const [subjects, setSubjects] = useState<StudentSubject[]>([])
  const [subjectsLoading, setSubjectsLoading] = useState(true)
  const [subjectsError, setSubjectsError] = useState("")

  useEffect(() => {
    let cancelled = false
    setSubjectsLoading(true)
    setSubjectsError("")
    listStudentSubjects()
      .then((res) => {
        if (cancelled) return
        if (res.data?.success && Array.isArray(res.data.data)) {
          setSubjects(res.data.data)
        } else {
          setSubjects([])
          setSubjectsError(res.data?.message || "Could not load subjects")
        }
      })
      .catch(() => {
        if (cancelled) return
        setSubjects([])
        setSubjectsError("Could not load subjects")
      })
      .finally(() => {
        if (!cancelled) setSubjectsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selectedSubject = useMemo(
    () => subjects.find((s) => s.id === draft.subject) || null,
    [subjects, draft.subject]
  )

  const setField = <K extends keyof QuestionListFilter>(
    key: K,
    value: QuestionListFilter[K]
  ) => onChange({ ...draft, [key]: value })

  const handleSubjectChange = (subjectId: string) => {
    onChange({
      ...draft,
      subject: subjectId,
      hierarchy_ids: [],
      ...EMPTY_LEVEL_VALUES,
    })
  }

  return (
    <>
      {open && (
        <div
          className="questions-filter-backdrop"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={`questions-filter-panel ${open ? "is-open" : ""}`}
        aria-hidden={!open}
      >
        <div className="questions-filter-panel__header">
          <h2>
            <Filter size={18} className="is-primary" />
            Filters
          </h2>
          <button type="button" onClick={onClose} aria-label="Close filters">
            <X size={16} />
          </button>
        </div>

        <div className="questions-filter-panel__body">
          <section className="questions-filter-panel__section">
            <h3>Subject</h3>
            {subjectsLoading ? (
              <p className="questions-filter-panel__status">
                <Loader2 size={14} className="is-spinning" /> Loading subjects…
              </p>
            ) : subjectsError ? (
              <p className="questions-filter-panel__status is-error">
                {subjectsError}
              </p>
            ) : (
              <select
                value={draft.subject}
                onChange={(e) => handleSubjectChange(e.target.value)}
              >
                <option value="">All subjects</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
            )}
          </section>

          <section className="questions-filter-panel__section">
            <h3>Topics (Hierarchical)</h3>
            <LearnerTopicTree
              subjectId={draft.subject || null}
              levelLabels={selectedSubject?.level_labels}
              selectedIds={draft.hierarchy_ids || []}
              onChange={(hierarchy_ids) =>
                onChange({
                  ...draft,
                  hierarchy_ids,
                  ...EMPTY_LEVEL_VALUES,
                })
              }
            />
          </section>

          <section className="questions-filter-panel__section">
            <h3>Year &amp; Type</h3>
            <input
              type="text"
              inputMode="numeric"
              name="year"
              placeholder="e.g. 2024"
              value={draft.year}
              onChange={(e) =>
                setField("year", normalizeYearInput(e.target.value))
              }
              aria-label="Year"
            />
            <select
              className="is-spaced"
              value={draft.paper}
              onChange={(e) => setField("paper", e.target.value)}
            >
              <option value="">All Types</option>
              <option value="Past Paper">Past Papers</option>
              <option value="Practice">Practice</option>
            </select>
          </section>

          <div className="questions-filter-panel__mobile-apply">
            <button type="button" onClick={onApply}>
              Apply Filters
            </button>
            <button type="button" className="is-clear" onClick={onClear}>
              Clear
            </button>
          </div>

          <button
            type="button"
            className="questions-filter-panel__desktop-clear"
            onClick={onClear}
          >
            Clear filters
          </button>
        </div>
      </aside>
    </>
  )
}

export default QuestionsFilterPanel
